import * as Sentry from '@sentry/node'

import { CClient } from '@common/utils'
import { DEFAULT_USER_DETAILS } from '@common/constants'
import type { StudentPaymentSummary } from '@lib/api/billing'
import { isPlaceholderPhone } from '@lib/utils/identifier'

// NOTE: first_name is intentionally not sent in any conversion event.
// The email on the account belongs to the parent, while the name is the
// student's name. Sending the student's name against the parent's email
// actively misleads Google's user matching.

/**
 * Normalize email for Google Ads enhanced conversions.
 * Converts to lowercase and removes dots before @ symbol.
 * Returns undefined for dummy emails (e.g. @codingal-student.com) used for
 * users who haven't set a real email address.
 * Google will automatically hash the normalized email.
 *
 * @param email - Email address to normalize
 * @returns Normalized email or undefined if input is empty or a dummy email
 */
export const normalizeEmailForTracking = (
  email?: string | null
): string | undefined => {
  if (!email) return undefined

  if (email.toLowerCase().endsWith(DEFAULT_USER_DETAILS.emailDomain))
    return undefined

  return email
    .toLowerCase()
    .replace(/^([^@]+)/, (match) => match.replace(/\./g, ''))
}

/**
 * Normalize phone number to E.164 format for Google Ads enhanced conversions.
 * Removes spaces, dashes, and parentheses. Adds + prefix if missing.
 * Google will automatically hash the normalized phone.
 *
 * @param phone - Phone number to normalize
 * @returns Normalized phone in E.164 format or undefined if input is empty
 */
export const normalizePhoneForTracking = (
  phone?: string | null
): string | undefined => {
  if (!phone) return undefined

  const cleaned = phone.replace(/[\s\-()]/g, '')
  const normalized = cleaned.startsWith('+') ? cleaned : `+${cleaned}`
  // An email signup's placeholder number belongs to nobody, so it would only
  // mismatch the user
  return isPlaceholderPhone(normalized) ? undefined : normalized
}

export const trackPageVisit = async (page: string, subject?: string) => {
  try {
    const response = await CClient('/api/v1/track-page-visit/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // camelCase per the repo convention; DRF's CamelCaseJSONParser turns
      // this into `extra_data` server-side. Matches the other caller of this
      // endpoint (`trackPageVisit` in @common/utils, which sends the course).
      body: JSON.stringify({
        page,
        ...(subject && { extraData: { subject } }),
      }),
    })
    return response.ok
  } catch (error) {
    Sentry.captureException(error)
    return false
  }
}

type SeenPageResult = {
  hasSeen: boolean
  // Subject the backend actually used to answer the check: either the one
  // passed in, or (on the `First class booked` / `Free class completed` pages,
  // when none was passed) one it inferred from the student's trial history.
  // `null` when the backend had nothing to scope by - the serializer sends an
  // explicit null, not a missing key - and absent when the request failed.
  subject?: string | null
}

const getSeenPageResult = async (
  page: string,
  token?: string,
  subject?: string,
  // Only the tracking pages themselves ask the backend to work out the subject
  // when they have none to send. Every other caller wants the unscoped
  // "has this student ever seen this page" answer, so this stays off by
  // default - see the `resolve_subject` parameter on the endpoint.
  resolveSubject = false
): Promise<SeenPageResult> => {
  try {
    const query = subject
      ? `page=${page}&subject=${encodeURIComponent(subject)}`
      : `page=${page}${resolveSubject ? '&resolve_subject=true' : ''}`
    const url = `/api/v1/track-page-visit/count/?${query}`
    const response = token
      ? await CClient(url, { headers: { Authorization: `Token ${token}` } })
      : await CClient(url)

    if (response.ok) {
      const data = await response.json()
      return { hasSeen: data.visitCount > 0, subject: data.subject }
    } else {
      throw new Error(response.statusText)
    }
  } catch (error) {
    Sentry.captureException(error)
    return { hasSeen: false }
  }
}

export const hasSeenPage = async (
  page: string,
  token?: string,
  subject?: string
): Promise<boolean> => {
  const result = await getSeenPageResult(page, token, subject)
  return result.hasSeen
}

// Same as `hasSeenPage`, but also returns the subject the backend actually
// used - needed by the trial-subject pages (track-booking / track-free-class)
// so the visit they go on to log can be tagged with the subject the backend
// resolved, rather than only whatever the frontend happened to know locally.
// Those two pages are also the only callers that want the backend to resolve a
// subject for them when they have none, hence `resolveSubject` on by default
// here and off in `hasSeenPage`.
export const hasSeenPageWithSubject = (
  page: string,
  token?: string,
  subject?: string
): Promise<SeenPageResult> => getSeenPageResult(page, token, subject, true)

/**
 * Whether the logged-in student has a course purchase still to be reported.
 *
 * `unsupported` means the API answered without the per-subject fields - either
 * an older backend or the kill switch being off - and the caller should fall
 * back to the legacy `hasSeenPage(TRACKED_PAGES.COURSE_PURCHASED)` gate. It must
 * never be read as `pending`, or a deploy skew would fire conversions blindly.
 */
export type CoursePurchaseGate =
  | {
      status: 'pending'
      subject: string
      valueUsd: number | null
      transactionId: string | null
    }
  | { status: 'none' }
  | { status: 'unsupported' }
  | { status: 'error' }

/**
 * Ask the server which course-purchase conversion, if any, is still pending.
 *
 * The server owns this decision so the dashboard redirect and the tracking page
 * cannot disagree and bounce the student between them.
 *
 * @param token - Auth token, required when called from getInitialProps
 * @returns The pending subject and its conversion value, or why there is none
 */
export const getCoursePurchaseGate = async (
  token?: string
): Promise<CoursePurchaseGate> => {
  try {
    const response = await CClient(
      '/api/v1/payment-pages/me/',
      token ? { headers: { Authorization: `Token ${token}` } } : undefined
    )

    if (!response.ok) return { status: 'error' }

    const data = (await response.json()) as StudentPaymentSummary

    if (!('pendingPurchaseSubject' in data)) return { status: 'unsupported' }
    if (!data.pendingPurchaseSubject) return { status: 'none' }

    return {
      status: 'pending',
      subject: data.pendingPurchaseSubject,
      valueUsd: data.subjectPaymentAmountUsd ?? null,
      transactionId: data.conversionTransactionId ?? null,
    }
  } catch (error) {
    Sentry.captureException(error)
    return { status: 'error' }
  }
}
